import { skipToken } from "@reduxjs/toolkit/query"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { resolveMobCardImage } from "@/helpers/mobImage"
import { ServerDate } from "@/helpers/server-date.class"
import { showToast } from "@/helpers/toast"
import { useBattleCountdown } from "@/hooks/useBattleCountdown"
import {
  actionRequestFor,
  battleEndStep,
  canHeroAct,
  deriveBattleActions,
  deriveBattleField,
  deriveMoveTargets,
  errorStatusOf,
  fighterClassKeys,
  fighterVitals,
  formatRoundLog,
  heroOf,
  mobOf,
  rereadDelayMs,
  screenStatusOf
} from "@/hooks/useBattleScreen/helpers"
import {
  IBattleActionChoice,
  IBattleActionsModel,
  IBattleFieldCellView,
  IBattleFighterView,
  IBattleScreenView,
  IRoundLogLine
} from "@/hooks/useBattleScreen/types"
import { useModals } from "@/modals/hooks"
import { useAppDispatch } from "@/store"
import { TAGS } from "@/store/api"
import {
  battleAPI,
  rereadBattle,
  useFleeMutation,
  useGetBattleQuery,
  useGetLegalActionsQuery,
  useSetAutoMutation,
  useSubmitActionMutation
} from "@/store/apis/battleAPI/battle.api"
import {
  BATTLE_MOVE_DIRECTION,
  BATTLE_STATE
} from "@/store/apis/battleAPI/battle.types"
import {
  battleEndInvalidates,
  battleEnds
} from "@/store/apis/battleAPI/battleEnd"
import { battleFailureOf } from "@/store/apis/battleAPI/battleFailure"
import {
  mobsAPI,
  useGetOpenEngagementQuery
} from "@/store/apis/mobsAPI/mobs.api"
import { useGetMeQuery } from "@/store/apis/playerAPI"

const EMPTY_LOG: IRoundLogLine[] = []
const EMPTY_FIELD: IBattleFieldCellView[] = []

const busyOnly = ({ isLoading }: { isLoading: boolean }) => ({ isLoading })

export const useBattleScreen = (battleId: string): IBattleScreenView => {
  const { t } = useTranslation("battle")
  const dispatch = useAppDispatch()
  const { openModal, closeModal } = useModals()

  const { view, isError, errorStatus, refetch } = useGetBattleQuery(battleId, {
    selectFromResult: ({ currentData, isError, error }) => ({
      view: currentData,
      isError,
      errorStatus: errorStatusOf(error)
    })
  })
  const isOpen = view?.state === BATTLE_STATE.OPEN
  const { legal } = useGetLegalActionsQuery(
    view && isOpen ? { battleId, round: view.round } : skipToken,
    { selectFromResult: ({ currentData }) => ({ legal: currentData }) }
  )
  const { engagement } = useGetOpenEngagementQuery(undefined, {
    refetchOnMountOrArgChange: true,
    selectFromResult: ({ currentData, isFetching }) => ({
      engagement: isFetching ? null : (currentData?.engagement ?? null)
    })
  })
  const { username } = useGetMeQuery(undefined, {
    selectFromResult: ({ data }) => ({ username: data?.username ?? null })
  })

  const [submitAction, { isLoading: submitBusy }] = useSubmitActionMutation({
    selectFromResult: busyOnly
  })
  const [setAuto, { isLoading: autoBusy }] = useSetAutoMutation({
    selectFromResult: busyOnly
  })
  const [flee, { isLoading: fleeBusy }] = useFleeMutation({
    selectFromResult: busyOnly
  })
  const busy = submitBusy || autoBusy || fleeBusy

  const screenStatus = screenStatusOf({ view, isError, errorStatus })

  useEffect(() => {
    if (screenStatus !== "gone") {
      return
    }
    dispatch(
      battleAPI.util.updateQueryData("getOpenBattle", undefined, (draft) =>
        draft.battle?.id === battleId ? { battle: null } : draft
      )
    )
    closeModal("battleScreen")
    dispatch(battleAPI.util.invalidateTags([TAGS.BATTLE]))
  }, [screenStatus])

  useEffect(() => {
    if (!view) {
      return
    }
    const step = battleEndStep(view, battleEnds.claim)
    if (!step) {
      return
    }
    dispatch(
      battleAPI.util.updateQueryData("getOpenBattle", undefined, () => ({
        battle: null
      }))
    )
    closeModal("battleScreen")
    const { followUp } = step
    if (!followUp) {
      return
    }
    dispatch(mobsAPI.util.invalidateTags(battleEndInvalidates(followUp)))
    if (followUp.kind === "reward") {
      openModal("mobReward", { killId: followUp.killId })
    } else {
      openModal("battleResult", {
        outcome: followUp.outcome,
        endReason: followUp.endReason
      })
    }
  }, [view?.id, view?.state])

  const [rereadPulse, setRereadPulse] = useState(0)

  useEffect(() => {
    const delay = rereadDelayMs(view, ServerDate.now())
    if (delay === null) {
      return
    }
    const timer = setTimeout(() => {
      void dispatch(rereadBattle(battleId))
      setRereadPulse((pulse) => pulse + 1)
    }, delay)
    return () => clearTimeout(timer)
  }, [
    battleId,
    view?.state,
    view?.round,
    view?.roundDeadline,
    view?.auto,
    rereadPulse
  ])

  const onFailure = useCallback(
    (error: unknown) => {
      const failure = battleFailureOf(error)
      if (failure === "REREAD") {
        void dispatch(rereadBattle(battleId))
        return
      }
      showToast.error(
        t(
          failure === "REJECTED"
            ? "battle:toast.notPossible"
            : "battle:toast.failed"
        )
      )
    },
    [dispatch, battleId, t]
  )

  const act = useCallback(
    (choice: IBattleActionChoice) => {
      if (!view || !legal) {
        return
      }
      const action = actionRequestFor(choice, view, legal)
      if (!action) {
        return
      }
      submitAction({ battleId, round: view.round, action })
        .unwrap()
        .catch(onFailure)
    },
    [view, legal, battleId, submitAction, onFailure]
  )

  const onAttack = useCallback(() => act({ kind: "attack" }), [act])
  const onAbility = useCallback(
    (abilityId: string) => act({ kind: "ability", abilityId }),
    [act]
  )
  const onMove = useCallback(
    (direction: BATTLE_MOVE_DIRECTION) => act({ kind: "move", direction }),
    [act]
  )

  const onToggleAuto = useCallback(() => {
    if (!view) {
      return
    }
    setAuto({ battleId, enabled: !view.auto }).unwrap().catch(onFailure)
  }, [view, battleId, setAuto, onFailure])

  const onFlee = useCallback(() => {
    flee(battleId).unwrap().catch(onFailure)
  }, [battleId, flee, onFailure])

  const retry = useCallback(() => {
    void refetch()
  }, [refetch])

  const heroFighter = view ? heroOf(view) : undefined
  const mobFighter = view ? mobOf(view) : undefined
  const typeCode = engagement?.typeCode ?? null

  const hero = useMemo(
    (): IBattleFighterView => ({
      kind: "HERO",
      username,
      imageUrl: undefined,
      vitals: heroFighter
        ? fighterVitals(heroFighter, { withEnergy: true })
        : [],
      classKeys: fighterClassKeys(heroFighter)
    }),
    [username, heroFighter]
  )

  const mob = useMemo(
    (): IBattleFighterView => ({
      kind: "MOB",
      typeCode,
      level: engagement?.level ?? null,
      imageUrl: typeCode ? resolveMobCardImage(typeCode) : undefined,
      vitals: mobFighter
        ? fighterVitals(mobFighter, { withEnergy: false })
        : [],
      classKeys: fighterClassKeys(mobFighter)
    }),
    [typeCode, engagement?.level, mobFighter]
  )

  const log = useMemo(
    () =>
      view ? formatRoundLog(view.lastRound, view.heroFighterId) : EMPTY_LOG,
    [view?.lastRound, view?.heroFighterId]
  )

  const field = useMemo(
    () =>
      view
        ? deriveBattleField(
            view,
            deriveMoveTargets(view, legal, canHeroAct({ view, legal, busy }))
          )
        : EMPTY_FIELD,
    [view, legal, busy]
  )

  const actions = useMemo(
    (): IBattleActionsModel =>
      view
        ? deriveBattleActions({ view, legal, busy })
        : {
            attack: { enabled: false },
            abilities: [],
            autoOn: false,
            autoEnabled: false,
            fleeEnabled: !busy
          },
    [view, legal, busy]
  )

  const autoOn = view?.auto ?? false
  const timerText = useBattleCountdown(
    view?.roundDeadline ?? null,
    isOpen && !autoOn
  )

  return {
    status: screenStatus === "gone" ? "loading" : screenStatus,
    retry,
    round: view?.round ?? 0,
    autoOn,
    timerText,
    hero,
    mob,
    log,
    field,
    actions,
    onAttack,
    onAbility,
    onMove,
    onToggleAuto,
    onFlee
  }
}
